import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useWikiNav } from '@/components/layout/wikiNavContext'
import type { WikiCategory, WikiPage } from '@/types/wiki'

function buildOrderedPages(pages: WikiPage[], categories: WikiCategory[]): WikiPage[] {
  const ordered: WikiPage[] = []

  // Uncategorized first (preserve API order).
  for (const p of pages) {
    if (!p.category_id) ordered.push(p)
  }

  // Then categories in navigation order, with pages preserving API order.
  for (const c of categories) {
    for (const p of pages) {
      if (p.category_id === c.id) ordered.push(p)
    }
  }

  return ordered
}

export default function PrevNextNav({ currentSlug }: { currentSlug: string }) {
  const { pages, categories, loading, error } = useWikiNav()

  const ordered = useMemo(() => buildOrderedPages(pages, categories), [pages, categories])
  const idx = useMemo(() => ordered.findIndex(p => p.slug === currentSlug), [ordered, currentSlug])

  if (loading || error || idx < 0) return null

  const prev = idx > 0 ? ordered[idx - 1] : null
  const next = idx >= 0 && idx < ordered.length - 1 ? ordered[idx + 1] : null
  if (!prev && !next) return null

  return (
    <nav className="mt-12 pt-8 border-t border-calamity-border-subtle/60">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {prev ? (
          <Link
            to={`/${prev.slug}`}
            className={`
              group flex-1 panel panel-inset p-5 transition-all
              hover:translate-y-[-1px] hover:brightness-[1.03]
              hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.09),inset_0_0_0_1px_rgba(149,89,216,0.48),0_22px_56px_rgba(0,0,0,0.44),0_0_22px_rgba(132,74,203,0.14)]
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-calamity-accent/50
            `}
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-calamity-text-muted font-bold">
              Previous
            </div>
            <div className="mt-3 flex items-center gap-3 text-calamity-text">
              <span className="text-calamity-text-muted group-hover:text-calamity-text transition-colors" aria-hidden="true">&larr;</span>
              <span className="font-display text-xl truncate">{prev.title}</span>
            </div>
          </Link>
        ) : (
          <div className="hidden sm:block flex-1" />
        )}

        {next ? (
          <Link
            to={`/${next.slug}`}
            className={`
              group flex-1 panel panel-inset p-5 transition-all sm:text-right
              hover:translate-y-[-1px] hover:brightness-[1.03]
              hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.09),inset_0_0_0_1px_rgba(149,89,216,0.48),0_22px_56px_rgba(0,0,0,0.44),0_0_22px_rgba(132,74,203,0.14)]
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-calamity-accent/50
            `}
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-calamity-text-muted font-bold">
              Next
            </div>
            <div className="mt-3 flex items-center gap-3 justify-between sm:justify-end text-calamity-text">
              <span className="font-display text-xl truncate">{next.title}</span>
              <span className="text-calamity-text-muted group-hover:text-calamity-text transition-colors" aria-hidden="true">&rarr;</span>
            </div>
          </Link>
        ) : (
          <div className="hidden sm:block flex-1" />
        )}
      </div>
    </nav>
  )
}
